import React from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { AnimatedSection, FadeUp } from "@/components/animated-section";
import { fadeUp, stagger } from "@/lib/motion";
import {
  ArrowRight,
  CalendarClock,
  GraduationCap,
  ShieldCheck,
  Blocks,
} from "lucide-react";

const DISCOVERY_MAILTO =
  "mailto:kylie@mainloopacademy.com?subject=Discovery%20call%20%E2%80%94%20MainLoop%20for%20Schools";

const PROGRAMS = [
  {
    num: "01",
    icon: Blocks,
    title: "Python Fundamentals",
    rows: [
      { label: "Length", value: "Built to fit an enrichment block or semester (12 sessions to 32 weeks)" },
      { label: "Best for", value: "Schools that want a foundational coding elective before layering in AI — the on-ramp track for classrooms with no prior CS experience" },
    ],
  },
  {
    num: "02",
    icon: CalendarClock,
    title: "AI Enrichment Club",
    rows: [
      { label: "Length", value: "12 sessions" },
      { label: "Aligned to", value: "AI4K12\u2019s Five Big Ideas" },
      { label: "Best for", value: "After-school clubs, short enrichment blocks, a first taste of AI before a bigger commitment" },
    ],
  },
  {
    num: "03",
    icon: GraduationCap,
    title: "Intro to AI",
    rows: [
      { label: "Length", value: "32 weeks, full-year" },
      { label: "Best for", value: "A semester or year-long elective, a parent-facing enrichment offering, deeper skill-building" },
    ],
  },
  {
    num: "04",
    icon: ShieldCheck,
    title: "Hybrid",
    rows: [
      { label: "Length", value: "Built to your calendar — e.g., an 8-session cut of Intro to AI, or a Python Fundamentals + AI Enrichment Club combo" },
      { label: "Best for", value: "A schedule that doesn\u2019t cleanly fit any single program above" },
    ],
  },
];

const FORMATS = [
  {
    title: "Teacher-led",
    body: "Your own staff teaches, using a complete curriculum package: slides, lesson plans, projects, and rubrics, built so no prior AI or Python background is required. We train your team before launch.",
  },
  {
    title: "Self-paced",
    body: "Students move through video lessons independently, in the LMS you already use, with light-touch check-ins.",
  },
  {
    title: "Hybrid",
    body: "A blend of teacher-led sessions and self-paced weeks, matched to your calendar.",
  },
];

const STEPS = [
  {
    title: "Book a discovery call.",
    body: "30 minutes on your students, your schedule, and what success looks like to you — an elective grade, a competition pipeline, a parent-facing offering, teacher PD, whatever matters to you.",
  },
  {
    title: "Get a specific proposal.",
    body: "One priced package — program, format, session count, what\u2019s included. Not a menu.",
  },
  {
    title: "We handle the paperwork.",
    body: "Data privacy agreement, W-9, insurance — we bring it, you review and sign.",
  },
  {
    title: "We build your curriculum.",
    body: "Session count, terminology, even co-branded materials — matched to your schedule and standards, not our defaults.",
  },
  {
    title: "We train your team and launch.",
    body: "A walkthrough with your staff, a tech check, materials loaded — no surprises in week one.",
  },
  {
    title: "You get a report.",
    body: "Recaps as you go, and an outcomes report at the end tied to whatever you told us mattered.",
  },
];

const FAQS = [
  {
    q: "Does my staff teach this, or does MainLoop?",
    a: "Your staff, using MainLoop\u2019s curriculum — full lesson plans, slides, and materials built so any teacher can run it without prior AI or Python experience. We train your team before launch and stay available for consulting support. If you\u2019d rather have a MainLoop engineer lead live sessions directly, that\u2019s available as an add-on.",
  },
  {
    q: "How do you handle student data and FERPA?",
    a: "We sign a Data Privacy Agreement before any roster, name, or email moves — yours if your district has a standard template, ours if it doesn\u2019t. It spells out exactly what we access, how it\u2019s used, and when it\u2019s destroyed.",
  },
  {
    q: "What happens if a session falls on a snow day or testing week?",
    a: "Rescheduling terms are built into the agreement before the program starts, so a missed session gets rescheduled or credited — not renegotiated on the fly.",
  },
  {
    q: "Do you work with homeschool co-ops and charter networks, or only traditional schools?",
    a: "All three. The curriculum, paperwork, and format flex to fit a single classroom, a multi-site charter network, or a homeschool co-op.",
  },
  {
    q: "What does this cost?",
    a: "Pricing depends on program, format, and group size — you\u2019ll get a specific number in your proposal after a quick discovery call, not a rate card.",
  },
];

function DiscoveryCta({ label = "Book a discovery call" }: { label?: string }) {
  return (
    <Button asChild size="lg" className="h-14 px-8 rounded-full text-base font-bold">
      <a href={DISCOVERY_MAILTO}>
        {label}
        <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
      </a>
    </Button>
  );
}

export default function ForSchools() {
  return (
    <Layout>
      {/* HERO ─────────────────────────────────────── */}
      <section className="border-b border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-50" aria-hidden="true" />
        <div className="container mx-auto px-4 py-24 md:py-36 relative z-10">
          <motion.div variants={stagger(0.1)} initial="hidden" animate="visible" className="max-w-5xl">
            <motion.div variants={fadeUp} className="section-tag mb-6">
              [ FOR SCHOOLS ] · Vol. 01 / Curriculum built by people who build
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="font-display text-[2.75rem] sm:text-6xl md:text-[5.5rem] font-bold leading-[0.98] tracking-[-0.035em] mb-10 text-balance"
            >
              Real AI and Python curriculum. Built by engineers,{" "}
              <span className="font-italic-serif font-normal text-primary">ready</span> for your teachers.
            </motion.h1>
            <motion.p variants={fadeUp} className="text-xl md:text-2xl text-muted-foreground max-w-3xl leading-relaxed mb-10">
              MainLoop builds AI and Python curriculum for schools, charter networks, and homeschool co-ops — full lesson plans, projects, and materials your own team can teach, with consulting support when you want it.
            </motion.p>
            <motion.div variants={fadeUp}>
              <DiscoveryCta />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* [ 01 ] WHY MAINLOOP ──────────────────────── */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="container mx-auto px-4">
          <AnimatedSection className="max-w-4xl space-y-8" staggerChildren={0.1}>
            <FadeUp>
              <div className="section-tag">[ 01 ] · Why MainLoop</div>
            </FadeUp>
            <FadeUp>
              <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight text-balance">
                Not generic <span className="font-italic-serif font-normal text-primary">STEM</span> curriculum.
              </h2>
            </FadeUp>
            <FadeUp>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                MainLoop&rsquo;s programs are written by working engineers, not curriculum contractors assembling worksheets from a template — the same standard we hold in our own paid cohorts. That means your teachers get lesson plans, projects, and materials built around the tools real engineers actually use — real Python, real AI concepts, real production tools — not a simplified stand-in for them. You don&rsquo;t need an AI background on staff to run it: we build the curriculum to be teachable, and we&rsquo;re available to train your team and consult as you go.
              </p>
            </FadeUp>
          </AnimatedSection>
        </div>
      </section>

      {/* [ 02 ] PROGRAM COMPARISON ────────────────── */}
      <section className="py-24 md:py-32 border-b border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-dots pointer-events-none opacity-60" aria-hidden="true" />
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="max-w-3xl mb-16" staggerChildren={0.1}>
            <FadeUp>
              <div className="section-tag mb-4">[ 02 ] · Program comparison</div>
            </FadeUp>
            <FadeUp>
              <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight text-balance">
                Three programs. Pick the one that{" "}
                <span className="font-italic-serif font-normal text-primary">fits</span> your calendar.
              </h2>
            </FadeUp>
          </AnimatedSection>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border" staggerChildren={0.1}>
            {PROGRAMS.map(p => {
              const Icon = p.icon;
              return (
                <motion.div key={p.num} variants={fadeUp} className="bg-background p-8 md:p-10">
                  <div className="flex items-center justify-between mb-8">
                    <div className="w-12 h-12 bg-primary/10 text-primary rounded-md flex items-center justify-center">
                      <Icon className="w-6 h-6" aria-hidden="true" />
                    </div>
                    <span className="font-display text-5xl font-bold text-muted-foreground/30 leading-none">{p.num}</span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-bold mb-6 tracking-tight">{p.title}</h3>
                  <dl className="space-y-4">
                    {p.rows.map(row => (
                      <div key={row.label}>
                        <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{row.label}</dt>
                        <dd className="text-foreground/90 leading-relaxed">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                </motion.div>
              );
            })}
          </AnimatedSection>

          <AnimatedSection className="mt-8">
            <FadeUp>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Every program is delivered as a full curriculum package — see &ldquo;Fits your schedule&rdquo; below for how it gets taught.
              </p>
            </FadeUp>
          </AnimatedSection>
        </div>
      </section>

      {/* [ 03 ] FITS YOUR SCHEDULE ────────────────── */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="container mx-auto px-4">
          <AnimatedSection className="max-w-3xl mb-16" staggerChildren={0.1}>
            <FadeUp>
              <div className="section-tag mb-4">[ 03 ] · Fits your schedule</div>
            </FadeUp>
            <FadeUp>
              <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight text-balance">
                Teacher-led, self-paced, or <span className="font-italic-serif font-normal text-primary">both.</span>
              </h2>
            </FadeUp>
            <FadeUp>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                Three ways to run it, one goal: fit the program to your calendar and your staff, not the other way around.
              </p>
            </FadeUp>
          </AnimatedSection>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-3 gap-8" staggerChildren={0.12}>
            {FORMATS.map((f, i) => (
              <motion.div key={f.title} variants={fadeUp} className="border-t-2 border-foreground pt-6">
                <div className="font-mono text-xs text-muted-foreground mb-3">0{i + 1}</div>
                <h3 className="font-display text-xl md:text-2xl font-bold mb-3 tracking-tight">{f.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </AnimatedSection>

          <AnimatedSection className="mt-12">
            <FadeUp>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
                Want a MainLoop engineer to lead live sessions directly, instead of your own staff? That&rsquo;s available as an add-on —{" "}
                <span className="text-foreground font-medium">ask on your discovery call.</span>
              </p>
            </FadeUp>
          </AnimatedSection>
        </div>
      </section>

      {/* [ 04 ] HOW IT WORKS ──────────────────────── */}
      <section className="py-24 md:py-32 bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-sm opacity-[0.06] pointer-events-none" aria-hidden="true" />
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="max-w-3xl mb-16" staggerChildren={0.1}>
            <FadeUp>
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-4">[ 04 ] · How it works</div>
            </FadeUp>
            <FadeUp>
              <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight text-balance">
                What happens after you say <span className="font-italic-serif text-secondary">yes.</span>
              </h2>
            </FadeUp>
          </AnimatedSection>

          <AnimatedSection staggerChildren={0.08}>
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 mb-16 list-none">
              {STEPS.map((s, i) => (
                <motion.li key={s.title} variants={fadeUp}>
                  <div className="font-display text-5xl font-bold text-background/25 leading-none mb-4" aria-hidden="true">0{i + 1}</div>
                  <h3 className="font-display text-xl font-bold mb-2 tracking-tight">{s.title}</h3>
                  <p className="text-background/70 leading-relaxed">{s.body}</p>
                </motion.li>
              ))}
            </ol>
            <FadeUp>
              <Button asChild size="lg" variant="secondary" className="h-14 px-8 rounded-full text-base font-bold">
                <a href={DISCOVERY_MAILTO}>
                  Book a discovery call
                  <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
                </a>
              </Button>
            </FadeUp>
          </AnimatedSection>
        </div>
      </section>

      {/* [ 05 ] TRUST & COMPLIANCE ────────────────── */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="container mx-auto px-4">
          <AnimatedSection className="max-w-4xl space-y-8" staggerChildren={0.1}>
            <FadeUp>
              <div className="section-tag">[ 05 ] · Trust &amp; compliance</div>
            </FadeUp>
            <FadeUp>
              <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.1] tracking-tight text-balance">
                <span className="text-marker">We bring the paperwork.</span> You approve it.
              </h2>
            </FadeUp>
            <FadeUp>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Vendor onboarding is usually the real bottleneck, not the sales pitch. MainLoop arrives insured and FERPA-compliant — a signed Data Privacy Agreement covers any student data we touch (rosters, logins, progress data), $1M/$2M general liability names your school as additional insured, and a W-9 is ready before your business office asks for it. If you bring us in for live sessions or on-site consulting, background-checked support is available too. You&rsquo;re not chasing us for paperwork; we send it before you need it.
              </p>
            </FadeUp>
          </AnimatedSection>
        </div>
      </section>

      {/* [ 06 ] PROOF IT WORKED ───────────────────── */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="container mx-auto px-4">
          <AnimatedSection className="max-w-4xl space-y-8" staggerChildren={0.1}>
            <FadeUp>
              <div className="section-tag">[ 06 ] · Proof it worked</div>
            </FadeUp>
            <FadeUp>
              <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight text-balance">
                You&rsquo;ll know it worked — because we{" "}
                <span className="font-italic-serif font-normal text-primary">show</span> you.
              </h2>
            </FadeUp>
            <FadeUp>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Success means something different at every school: a grade on a transcript, a competition pipeline, something to show parents, a PD credit for a teacher. We ask what it means to you on the first call, then build the reporting around that answer. At the end of the engagement — and at the midpoint, for anything longer than a semester — you get a short report: completion rates, project rubric scores, real student work samples, and feedback, addressed to whoever actually needs the evidence, not just your day-to-day contact.
              </p>
            </FadeUp>
          </AnimatedSection>
        </div>
      </section>

      {/* [ 07 ] FAQ ───────────────────────────────── */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <AnimatedSection className="lg:col-span-4 lg:sticky lg:top-28" staggerChildren={0.1}>
            <FadeUp>
              <div className="section-tag mb-4">[ 07 ] · FAQ</div>
            </FadeUp>
            <FadeUp>
              <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.1] tracking-tight text-balance">
                Questions your business office will{" "}
                <span className="font-italic-serif font-normal text-primary">ask.</span>
              </h2>
            </FadeUp>
          </AnimatedSection>

          <AnimatedSection className="lg:col-span-8 divide-y divide-border border-y border-border" staggerChildren={0.08}>
            {FAQS.map(f => (
              <motion.div key={f.q} variants={fadeUp} className="py-8">
                <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight mb-3">{f.q}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.a}</p>
              </motion.div>
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* [ 08 ] FINAL CTA ─────────────────────────── */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-50" aria-hidden="true" />
        <div className="container mx-auto px-4 relative z-10 text-center">
          <AnimatedSection className="max-w-3xl mx-auto space-y-8" staggerChildren={0.1}>
            <FadeUp>
              <div className="section-tag">[ 08 ] · Final CTA</div>
            </FadeUp>
            <FadeUp>
              <h2 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight text-balance">
                See if it <span className="font-italic-serif font-normal text-primary">fits.</span>
              </h2>
            </FadeUp>
            <FadeUp>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                One call tells you whether this fits your students, your schedule, and your budget cycle. No proposal, no pressure.
              </p>
            </FadeUp>
            <FadeUp>
              <DiscoveryCta />
            </FadeUp>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
